/**
 * Game i18n - โหลดและใช้คำแปลจากข้อมูลเกม Pokemon GO
 * ใช้ไฟล์ i18n_english.json และ i18n_thai.json
 *
 * โครงสร้างไฟล์: {"data": ["key1", "value1", "key2", "value2", ...]}
 */

import React from 'react';

const I18N_EN_URL = '/i18n/i18n_english.json';
const I18N_TH_URL = '/i18n/i18n_thai.json';

let cacheEn = null;
let cacheTh = null;
let loadingPromise = null;
const listeners = new Set();

// Reverse lookup caches: English move name → move key
let reverseMoveCacheEn = null;

/**
 * แปลง array คู่ key-value เป็น object
 */
function parseGameI18nData(arr) {
  if (!arr || !Array.isArray(arr)) return {};
  const result = {};
  for (let i = 0; i < arr.length - 1; i += 2) {
    result[arr[i]] = arr[i + 1] || '';
  }
  return result;
}

/**
 * โหลดทั้งสองภาษาพร้อมกัน
 */
async function loadGameI18n() {
  if (cacheEn && cacheTh) {
    return { en: cacheEn, th: cacheTh };
  }
  if (loadingPromise) return loadingPromise;

  loadingPromise = Promise.all([
    fetch(I18N_EN_URL).then(r => r.json()),
    fetch(I18N_TH_URL).then(r => r.json()),
  ])
    .then(([dataEn, dataTh]) => {
      cacheEn = parseGameI18nData(dataEn.data);
      cacheTh = parseGameI18nData(dataTh.data);
      listeners.forEach(fn => fn());
      listeners.clear();
      return { en: cacheEn, th: cacheTh };
    })
    .catch(err => {
      console.error('[GAME I18N] Failed to load:', err);
      cacheEn = cacheEn || {};
      cacheTh = cacheTh || {};
      loadingPromise = null;
      return { en: cacheEn, th: cacheTh };
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
}

/**
 * แปลง Pokemon ID ของ pogodex เป็น game key
 * ตัวอย่าง: "1" -> "pokemon_name_0001", "6-Mega_X" -> "pokemon_name_0006_0002"
 */
const VARIANT_TO_FORM = {
  mega: '0001',
  mega_x: '0002',
  mega_y: '0003',
  gigantamax: '0959_2', // หรือ 0187_2 ขึ้นกับโปเกมอน
  alolan: '0019',  // Alola form ID ในเกม
  galarian: '0078',
  hisuian: '0058',
  paldean: '0031',
};

/**
 * Map pogodex Pokemon ID เป็น game i18n key
 */
export function pokemonIdToGameKey(pokemonId) {
  if (!pokemonId) return null;
  const str = String(pokemonId).trim();

  // รูปแบบ "19" หรือ "0019"
  if (/^\d+$/.test(str)) {
    const padded = str.padStart(4, '0');
    return `pokemon_name_${padded}`;
  }

  // รูปแบบ "3-Mega", "6-Mega_X", "12-Gigantamax", "19-Alola"
  const dashMatch = str.match(/^(\d+)-(.+)$/i);
  if (dashMatch) {
    const baseId = dashMatch[1].padStart(4, '0');
    const variantPart = dashMatch[2].toLowerCase().replace(/\s/g, '_');

    if (variantPart.includes('mega_x') || variantPart === 'mega-x') {
      return `pokemon_name_${baseId}_0002`;
    }
    if (variantPart.includes('mega_y') || variantPart === 'mega-y') {
      return `pokemon_name_${baseId}_0003`;
    }
    if (variantPart.includes('gigantamax') || variantPart.includes('gmax')) {
      const gmaxFormIds = { '0003': '0169_2', '0006': '0178_2', '0009': '0187_2', '0012': '0959_2', '0025': '0598_2', '0052': '0063_2', '0068': '0815_2', '0094': '1044_2', '0099': '0873_2', '0131': '0322_2', '0133': '1092_2', '0143': '0199_2' };
      const form = gmaxFormIds[baseId] || '0187_2';
      return `pokemon_name_${baseId}_${form}`;
    }
    if (variantPart.includes('alola') || variantPart.includes('alolan')) {
      // เกมอาจใช้ _alolan (CSV) หรือ _0019 - ลอง _alolan ก่อน
      return `pokemon_name_${baseId}_alolan`;
    }
    if (variantPart.includes('galar')) {
      return `pokemon_name_${baseId}_galarian`;
    }
    if (variantPart.includes('hisui')) {
      return `pokemon_name_${baseId}_0058`;
    }
    if (variantPart.includes('mega')) {
      return `pokemon_name_${baseId}_0001`;
    }

    // Fallback: ใช้ base เท่านั้น
    return `pokemon_name_${baseId}`;
  }

  // รูปแบบ "0038_alolan", "38_alolan", "ninetales_alolan"
  const underMatch = str.match(/^(\d+)_(.+)$/i);
  if (underMatch) {
    const baseId = underMatch[1].padStart(4, '0');
    const variantPart = underMatch[2].toLowerCase();
    if (variantPart.includes('alola')) return `pokemon_name_${baseId}_alolan`;
    if (variantPart.includes('galar')) return `pokemon_name_${baseId}_galarian`;
    return `pokemon_name_${baseId}`;
  }

  return null;
}

/**
 * Map move ID เป็น game key: move_name_0001
 */
export function moveIdToGameKey(moveId) {
  if (moveId == null) return null;
  const num = parseInt(String(moveId).replace(/\D/g, ''), 10);
  if (isNaN(num)) return null;
  const padded = String(num).padStart(4, '0');
  return `move_name_${padded}`;
}

/**
 * ค้นหาคำแปลจาก game i18n
 */
export function gameT(key, lang = 'en') {
  const cache = lang === 'th' ? cacheTh : cacheEn;
  if (!cache) return key;
  const val = cache[key];
  return val !== undefined && val !== '' ? val : key;
}

/**
 * ได้ชื่อโปเกมอนจาก game i18n ตาม Pokemon ID
 * ถ้าไม่มีคำแปล จะ fallback ไป base form
 */
export function getGamePokemonName(pokemonId, lang = 'en') {
  const key = pokemonIdToGameKey(pokemonId);
  if (!key) return null;
  let name = gameT(key, lang);
  if (name === key) {
    const match = key.match(/^(pokemon_name_\d{4})/);
    if (match) name = gameT(match[1], lang);
  }
  return name && name !== key ? name : null;
}

/**
 * ได้ชื่อท่าต่อสู้จาก game i18n (โดยใช้ move ID ตัวเลข)
 */
export function getGameMoveName(moveId, lang = 'en') {
  const key = moveIdToGameKey(moveId);
  if (!key) return null;
  const name = gameT(key, lang);
  return name && name !== key ? name : null;
}

/**
 * สร้าง reverse cache: English move name (lowercase) → move_name_XXXX key
 */
function buildReverseMoveCache() {
  if (reverseMoveCacheEn || !cacheEn) return;
  reverseMoveCacheEn = {};
  for (const [key, value] of Object.entries(cacheEn)) {
    if (key.startsWith('move_name_') && value) {
      reverseMoveCacheEn[value.toLowerCase().trim()] = key;
    }
  }
}

/**
 * แปลชื่อท่าจาก English name ไปเป็นภาษาที่ต้องการ
 * เช่น translateMoveName("Counter", "th") → "เคาน์เตอร์"
 */
export function translateMoveName(englishName, lang = 'en') {
  if (!englishName || lang === 'en') return englishName;
  buildReverseMoveCache();
  if (!reverseMoveCacheEn) return englishName;
  
  const normalizedName = englishName.toLowerCase().trim();
  const key = reverseMoveCacheEn[normalizedName];
  if (!key) return englishName; // No key found, return original

  const translated = gameT(key, lang);
  return (translated && translated !== key) ? translated : englishName;
}

/**
 * โหลด game i18n และ return promise
 */
export function initializeGameI18n() {
  return loadGameI18n();
}

/**
 * Hook สำหรับรอโหลด game i18n แล้ว re-render
 */
export function useGameI18nReady() {
  const [ready, setReady] = React.useState(!!(cacheEn && cacheTh));

  React.useEffect(() => {
    if (cacheEn && cacheTh) {
      setReady(true);
      return;
    }
    const listener = () => setReady(true);
    listeners.add(listener);
    loadGameI18n().then(() => setReady(true));
    return () => listeners.delete(listener);
  }, []);

  return ready;
}

